import { FileUploadContext } from '@/context/file-upload-context'
import { PaperclipAttachment } from '@/assets/icons'
import * as Popover from '@radix-ui/react-popover'
import { useContext, useState, useEffect, useRef, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import AssistantDocumentSelectionDialog, { AssistantDocumentSelectionDialogDriveMode } from '../../source-dialogs/SelectionDialogAssistant'
import { Folder, Upload, ChevronRight } from '@/assets/icons'
import { selectAgentConversationFormTemplateFilePaths, selectAgentConversationFormWritingSampleFileIds } from '@/agent/chat_form/store/selectors'
import { selectAgentConversationMatterId } from '@/agent/conversations/store/selectors'
import { selectActiveMatterId } from '@/matter/store/selectors'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent, TemplateSource } from '@/analytics/schema/events.schema'
import { AgentConversationFormActions } from '@/agent/chat_form/store/slice'
import FilePill from '@/chat-common/selected_files/components/FilePill'
import { brandedAIFriendlyName } from '@/util/enterprise'
import { FileProcessingStatus } from '@/store/slices/file-upload-tasks.slice'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { Button } from '@/components/button/Button'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { matterPaths } from '@/routing/paths'
import { ADD_FILES_MODAL_TAB, type AddFilesModalInitialTab } from '../../source-dialogs/AddFilesModal'
import { useExternalStorageMenuStatus, type ExternalStorageMenuStatus } from '@/user_files/external-storage/hooks/useExternalStorageMenuStatus'
import { FilePickerSecurityFooter } from '@/user_files/external-storage/components/FilePickerSecurityFooter'

type FileActionMenuProps = {
  disabled?: boolean
  conversationId: string
}

enum FileAction {
  UploadLegacy = 'upload-legacy',
  UploadNew = 'upload-new',
  SelectLegacy = 'select-legacy',
  SelectNew = 'select-new',
}

enum TabType {
  AddFiles = 'add-files',
  ApplyTemplate = 'apply-template',
}

function getMenuItemClassName(disabled: boolean, hasDescription: boolean) {
  return `flex w-full items-center gap-2.5 rounded-md text-left ${hasDescription ? 'min-h-10 px-2 py-0' : 'p-2'} ${
    disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-brand-neutral-100'
  }`
}

function getTabClassName(active: boolean) {
  return `relative flex h-7 w-[158px] items-center justify-center px-4 py-1.5 text-[12px] font-semibold uppercase focus:outline-none ${
    active
      ? 'text-brand-neutral-950 after:absolute after:bottom-[-1px] after:left-0 after:h-[3px] after:w-full after:bg-brand-neutral-950'
      : 'text-brand-neutral-500'
  }`
}

const fileActionUploadLegacy = {
  value: FileAction.UploadLegacy,
  label: 'Upload Files',
  description: 'Select documents from your computer',
  icon: <Upload />,
}

const fileActionUploadNew = {
  value: FileAction.UploadNew,
  label: 'Upload Files',
  description: 'Select documents from your computer',
  icon: <Upload />,
}

const fileActionSelectLegacy = {
  value: FileAction.SelectLegacy,
  label: 'Add from Matter Files',
  description: 'Select from legacy Drive files',
  icon: <Folder />,
}

const fileActionSelectNew = {
  value: FileAction.SelectNew,
  label: 'Add from Matter Files',
  description: 'Select from new user files',
  icon: <Folder />,
}

const templateActions = [
  {
    value: FileAction.UploadLegacy,
    label: 'Upload File',
    icon: <Upload />,
  },
  {
    value: FileAction.SelectLegacy,
    label: 'Add from Matter Files',
    icon: <Folder />,
  },
]

type FileActionMenuItemProps = {
  icon: ReactNode
  label: string
  description?: string
  onClick: () => void
  ariaLabel?: string
  disabled?: boolean
  title?: string
  trailingIcon?: ReactNode
}

function FileActionMenuItem({ icon, label, description, onClick, ariaLabel, disabled = false, title, trailingIcon }: FileActionMenuItemProps) {
  return (
    <button
      className={getMenuItemClassName(disabled, !!description)}
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      role="menuitem"
      disabled={disabled}
      title={title}
    >
      <div className="shrink-0 text-brand-neutral-700 [&_svg]:h-4 [&_svg]:w-4">{icon}</div>
      {description ? (
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-0">
          <span className="text-sm font-medium leading-5 tracking-[-0.15px] text-brand-neutral-700">{label}</span>
          <span className="truncate text-xs font-normal leading-4 text-brand-neutral-600">{description}</span>
        </div>
      ) : (
        <span className="min-w-0 flex-1 text-sm font-medium leading-5 tracking-[-0.15px] text-brand-neutral-700">{label}</span>
      )}
      {trailingIcon && <div className="shrink-0 text-brand-neutral-700 [&_svg]:h-4 [&_svg]:w-4">{trailingIcon}</div>}
    </button>
  )
}

function ExternalStorageFileActionMenuItem({
  onConnectFolder,
  onOpenExternalFiles,
  status,
}: {
  onConnectFolder: () => void
  onOpenExternalFiles: () => void
  status: ExternalStorageMenuStatus
}) {
  if (!status.showMenuItem || !status.connectedProviderMeta || !status.ConnectedProviderIcon) {
    return null
  }
  const ConnectedProviderIcon = status.ConnectedProviderIcon

  if (status.actionKind === 'open-files') {
    return <FileActionMenuItem icon={<ConnectedProviderIcon />} label={`Add from ${status.connectedProviderMeta.label}`} onClick={onOpenExternalFiles} />
  }

  return (
    <FileActionMenuItem
      icon={<ConnectedProviderIcon />}
      label={`Connect a ${status.connectedProviderMeta.label} folder to this matter`}
      description="Attach synced files anywhere in this matter"
      onClick={onConnectFolder}
      trailingIcon={<ChevronRight />}
    />
  )
}

export default function FileActionMenu(props: FileActionMenuProps) {
  const { disabled, conversationId } = props
  const newFilesUxEnabled = kGetFeatureFlagStatus(kFeatureFlags.NEW_FILES_UX)
  const artifactTemplatesEnabled = kGetFeatureFlagStatus(kFeatureFlags.ARTIFACT_TEMPLATES)
  // Writing samples on the user_files pipeline are gated by their own flag (in addition to the
  // new files UX). When off, the template tab falls back to the legacy `template_paths` flow.
  const writingSampleFilesEnabled = newFilesUxEnabled && kGetFeatureFlagStatus(kFeatureFlags.WRITING_SAMPLE_FILES)
  const {
    openSystemFileSelector,
    openSystemTemplateFileSelector,
    openSystemUserFilesFileSelector,
    openSystemWritingSampleFileSelector,
    userFilesUploadDisabled,
    userFilesUploadDisabledReason,
  } = useContext(FileUploadContext)
  // New-vs-legacy switch: new upload + picker when the new files UX is on, the legacy ones otherwise.
  const addFilesActions = newFilesUxEnabled
    ? [{ ...fileActionUploadNew, disabled: userFilesUploadDisabled, title: userFilesUploadDisabledReason }, fileActionSelectNew]
    : [fileActionUploadLegacy, fileActionSelectLegacy]
  const [open, setPopoverOpen] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<TabType>(TabType.AddFiles)
  const [docSourceDialogIsOpen, setDocSourceDialogOpen] = useState<boolean>(false)
  const [driveSelectionMode, setDriveSelectionMode] = useState<AssistantDocumentSelectionDialogDriveMode>('new')
  const [initialAddFilesTab, setInitialAddFilesTab] = useState<AddFilesModalInitialTab>(ADD_FILES_MODAL_TAB.MATTER_FILES)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const prevTabRef = useRef<TabType>(activeTab)

  // Get template files (empty if not an agent conversation)
  const templateFiles = useAppSelector((state: RootState) => selectAgentConversationFormTemplateFilePaths(state, { conversationId }))
  const conversationMatterId = useAppSelector((state: RootState) => selectAgentConversationMatterId(state, { conversationId }))
  const activeMatterId = useAppSelector(selectActiveMatterId)
  const effectiveMatterId = conversationMatterId ?? activeMatterId
  const externalStorageAction = useExternalStorageMenuStatus({
    matterId: effectiveMatterId,
    menuOpen: open,
  })

  // Check if a template is already selected
  const hasTemplateFile = templateFiles.length > 0

  // New-files-UX writing samples (user_files). Selected sample renders in the main files bar.
  const writingSampleFileIds = useAppSelector((state: RootState) => selectAgentConversationFormWritingSampleFileIds(state, { conversationId }))
  const hasWritingSample = writingSampleFileIds.length > 0

  // Local helper: is the Apply Template tab active?
  const isTemplateContext = activeTab === TabType.ApplyTemplate

  // Update redux state whenever tab changes
  useEffect(() => {
    dispatch(AgentConversationFormActions.setTemplateMode(isTemplateContext))
  }, [activeTab, isTemplateContext, dispatch])

  // Analytics: track when the user opens/closes the Apply Template tab
  useEffect(() => {
    if (prevTabRef.current !== activeTab) {
      if (isTemplateContext) {
        trackEvent(AnalyticsEvent.ApplyTemplateTabOpened, { conversationId })
      } else if (prevTabRef.current === TabType.ApplyTemplate && activeTab === TabType.AddFiles) {
        trackEvent(AnalyticsEvent.ApplyTemplateTabClosed, { conversationId })
      }
    }
    prevTabRef.current = activeTab
  }, [activeTab, isTemplateContext, conversationId, trackEvent])

  const handleSelection = (action: FileAction) => {
    setPopoverOpen(false)

    if (action === FileAction.UploadLegacy) {
      if (isTemplateContext) {
        // Analytics: template mode upload
        trackEvent(AnalyticsEvent.TemplateSourceChosen, {
          conversationId,
          templateSourceType: TemplateSource.upload,
          isFirstTemplate: !hasTemplateFile,
        })
        // Open the template file selector
        openSystemTemplateFileSelector()
      } else {
        // Open the file selector
        openSystemFileSelector()
      }
    } else if (action === FileAction.UploadNew) {
      if (isTemplateContext) {
        // Writing-sample upload (the template tab under the new files UX).
        trackEvent(AnalyticsEvent.TemplateSourceChosen, {
          conversationId,
          templateSourceType: TemplateSource.upload,
          isFirstTemplate: !hasWritingSample,
        })
        openSystemWritingSampleFileSelector()
      } else {
        openSystemUserFilesFileSelector()
      }
    } else if (action === FileAction.SelectLegacy || action === FileAction.SelectNew) {
      if (isTemplateContext) {
        // Analytics: template mode drive select
        trackEvent(AnalyticsEvent.TemplateSourceChosen, {
          conversationId,
          templateSourceType: TemplateSource.drive,
          isFirstTemplate: writingSampleFilesEnabled ? !hasWritingSample : !hasTemplateFile,
        })
      }
      // Templates use the new My Drive picker only when the writing-sample-files flow is on;
      // otherwise (and for legacy brands) they stay on the legacy drive picker. Regular file
      // selection follows the chosen action.
      const useLegacyDrive = action === FileAction.SelectLegacy || (isTemplateContext && !writingSampleFilesEnabled)
      setDriveSelectionMode(useLegacyDrive ? 'legacy' : 'new')
      setInitialAddFilesTab(ADD_FILES_MODAL_TAB.MATTER_FILES)
      setDocSourceDialogOpen(true)
    }
  }

  const handleExternalFilesSelection = () => {
    setPopoverOpen(false)
    setDriveSelectionMode('new')
    setInitialAddFilesTab(ADD_FILES_MODAL_TAB.EXTERNAL_FILES)
    setDocSourceDialogOpen(true)
  }

  const handleConnectFolder = () => {
    setPopoverOpen(false)
    if (effectiveMatterId) {
      navigate(matterPaths.settings(effectiveMatterId))
    }
  }

  // Handler to clear the template file
  const handleRemoveTemplateFile = () => {
    if (hasTemplateFile) {
      dispatch(
        AgentConversationFormActions.removeTemplateFile({
          conversationId,
          filePath: templateFiles[0],
        })
      )
    }
  }

  // Handler for when a file is selected from the dialog
  const handleDialogClose = (value: boolean) => {
    setDocSourceDialogOpen(value)
  }

  return (
    <div className="flex">
      <Popover.Root open={open} onOpenChange={setPopoverOpen}>
        <Popover.Trigger asChild>
          <button
            onClick={() => setPopoverOpen(!open)}
            className={`${
              disabled ? 'opacity-50 cursor-default' : 'opacity-none hover:text-brand-500 cursor-pointer'
            } text-brand-neutral-800 w-[35px] h-[35px] inline-flex items-center justify-center`}
            aria-label="Add files by uploading or selecting from file manager"
            title="Add Files"
            disabled={disabled}
          >
            <PaperclipAttachment />
          </button>
        </Popover.Trigger>
        {!externalStorageAction.loading && (
          <Popover.Portal>
            <Popover.Content
              onEscapeKeyDown={() => setPopoverOpen(false)}
              onInteractOutside={() => setPopoverOpen(false)}
              className="PopoverContent z-50 w-[348px] mr-5 bg-white rounded-lg shadow-menu overflow-hidden"
            >
              <div className="flex flex-col">
                {/* Tab navigation to switch between Add Files and Writing Sample */}
                <div className="flex justify-center border-b border-brand-neutral-200 px-4 pt-1.5">
                  <button
                    className={getTabClassName(activeTab === TabType.AddFiles)}
                    onClick={() => {
                      setActiveTab(TabType.AddFiles)
                    }}
                  >
                    ADD FILES
                  </button>

                  <button
                    className={getTabClassName(isTemplateContext)}
                    onClick={() => {
                      setActiveTab(TabType.ApplyTemplate)
                    }}
                  >
                    {/* Today this feature is called "Apply Template". The new artifact templates feature (apply a template
                      at download) takes over that name when it ships, so this one is renamed to "Writing Sample" then. */}
                    {artifactTemplatesEnabled ? 'WRITING SAMPLE' : 'APPLY TEMPLATE'}
                  </button>
                </div>

                {/* Content based on active tab */}
                {activeTab === TabType.AddFiles && (
                  <div className="flex flex-col gap-2 p-2">
                    {addFilesActions.map((action) => (
                      <FileActionMenuItem
                        key={action.value}
                        icon={action.icon}
                        label={action.label}
                        onClick={() => handleSelection(action.value)}
                        disabled={'disabled' in action ? action.disabled : false}
                        title={'title' in action ? action.title : undefined}
                      />
                    ))}
                    <ExternalStorageFileActionMenuItem
                      status={externalStorageAction}
                      onOpenExternalFiles={handleExternalFilesSelection}
                      onConnectFolder={handleConnectFolder}
                    />
                  </div>
                )}

                {isTemplateContext && (
                  <>
                    {/* New writing-sample flow: a writing sample is a user_files file — either uploaded
                      from the user's computer or selected from the matter. When the flag is off (and
                      for legacy brands) the legacy template_paths flow below is used. */}
                    {writingSampleFilesEnabled ? (
                      <>
                        {/* Info prompt shown until a sample is selected */}
                        {!hasWritingSample && (
                          <div className="px-4 pt-4">
                            <div className="w-full rounded-lg border border-brand-neutral-200 bg-brand-neutral-50 px-3 py-3">
                              <p className="text-[12px] font-medium leading-[16px] text-brand-neutral-600">
                                Select a document for {brandedAIFriendlyName} to follow in style, tone, and reasoning.
                                {artifactTemplatesEnabled && (
                                  <span className="font-sans font-semibold text-xs leading-4 text-brand-800 mt-2"> Formatting is applied at download.</span>
                                )}
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="flex flex-col gap-2 p-2">
                          <FileActionMenuItem
                            icon={<Upload />}
                            label="Upload File"
                            onClick={() => handleSelection(FileAction.UploadNew)}
                            disabled={userFilesUploadDisabled}
                            title={userFilesUploadDisabledReason}
                          />
                          <FileActionMenuItem icon={<Folder />} label="Add from Matter Files" onClick={() => handleSelection(FileAction.SelectNew)} />
                          <ExternalStorageFileActionMenuItem
                            status={externalStorageAction}
                            onOpenExternalFiles={handleExternalFilesSelection}
                            onConnectFolder={handleConnectFolder}
                          />
                        </div>
                      </>
                    ) : newFilesUxEnabled ? (
                      /* New files UX with the writing-sample flag off: keep the pre-migration
                       "temporarily unavailable" state — the legacy template_paths flow below is the
                       non-Paxton path, never Paxton's. Drop this branch when the flag is removed. */
                      <div className="px-4 pt-4 pb-5 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <p className="text-[14px] font-semibold text-brand-neutral-900">This feature is temporarily unavailable</p>
                        </div>
                        <p className="text-[13px] text-brand-neutral-500 leading-[18px]">
                          We're updating how {brandedAIFriendlyName} handles references and templates.{' '}
                          {artifactTemplatesEnabled ? 'Writing samples will be back shortly.' : 'This feature will be back shortly.'}
                        </p>
                        <Button
                          variant="plain"
                          color="primary"
                          size="xs"
                          onClick={() => {
                            trackEvent(AnalyticsEvent.WritingSampleNotifyMeClicked, { conversationId })
                            dispatch(
                              openGlobalToast({
                                type: GlobalToastType.SUCCESS,
                                message: artifactTemplatesEnabled
                                  ? "We'll notify you when writing samples are available."
                                  : "We'll notify you when the Apply Template feature is available.",
                                durationMs: kToastDurationShortMs,
                              })
                            )
                          }}
                        >
                          Notify me
                        </Button>
                      </div>
                    ) : (
                      <>
                        {/* Show information container only when no template is applied */}
                        {!hasTemplateFile && (
                          <div className="px-4 pt-4">
                            <div className="w-full rounded-lg border border-brand-neutral-200 bg-brand-neutral-50 px-3 py-3">
                              <p className="text-[12px] font-medium leading-[16px] text-brand-neutral-600">
                                Upload a sample document for {brandedAIFriendlyName} to follow in style, tone, and reasoning.
                                {/* The artifact templates feature is what applies formatting at download, so only show
                                  this note once that feature is enabled. */}
                                {artifactTemplatesEnabled && (
                                  <span className="font-sans font-semibold text-xs leading-4 text-brand-800 mt-2"> Formatting is applied at download.</span>
                                )}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Show the template file if one is selected */}
                        {hasTemplateFile && (
                          <div className="p-4 pb-0">
                            <p className="text-xs font-semibold text-brand-neutral-600 mb-2">Selected Template (1 maximum)</p>
                            <FilePill
                              file={[templateFiles[0], FileProcessingStatus.SUCCESS]}
                              onRemoveFile={handleRemoveTemplateFile}
                              isTemplate={true}
                              conversationId={conversationId}
                            />
                          </div>
                        )}

                        {/* Always show template action buttons */}
                        <div className="flex flex-col gap-2 p-2">
                          {templateActions.map((action) => (
                            <FileActionMenuItem key={action.value} icon={action.icon} label={action.label} onClick={() => handleSelection(action.value)} />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}

                <FilePickerSecurityFooter variant={externalStorageAction.footerVariant} onNavigate={() => setPopoverOpen(false)} />
              </div>
            </Popover.Content>
          </Popover.Portal>
        )}
      </Popover.Root>
      <AssistantDocumentSelectionDialog
        open={docSourceDialogIsOpen}
        conversationId={conversationId}
        onClose={handleDialogClose}
        isTemplateMode={isTemplateContext}
        driveMode={driveSelectionMode}
        initialAddFilesTab={initialAddFilesTab}
      />
    </div>
  )
}
